import {PageTitle} from '_metronic/layout/core'
import {FC, useState, useEffect, Fragment, useRef} from 'react'
import HTMLRenderer from 'react-html-renderer'
import ReactQuill from 'react-quill'
import Api from '../../../thesis-and-dissertations/helpers/api'

import './style.css'
import UploadModal from './UploadModal'
import {Page, Document} from 'react-pdf'

import handleApiError from 'thesis-and-dissertations/helpers/handleApiError'
import {toast} from 'react-toastify'
import {number} from 'yup'
import {useNavigate} from 'react-router-dom'

import {Modal} from 'react-bootstrap'

const api = new Api()

type Props = {
  handleStepData: any
  errorProps: any
  asTeam: boolean // Deprecated - always false now (individu only)
  enabledSteps?: number[]
  currentStep?: number
}

const Step4Page: FC<Props> = ({handleStepData, errorProps, asTeam, enabledSteps = [3, 4, 5], currentStep = 4}) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<any>({})
  const [filePdf, setFilePdf] = useState('')
  const [dataPana, setDataPana] = useState<any>({})
  const [approved, setApproved] = useState<boolean>(false)
  const [isShow, setIsShow] = useState<boolean>(false)
  const [numPages, setNumPages] = useState(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>('')
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const [fieldRefs, setFieldRefs] = useState<any>({
    is_surat_pernyataan: useRef(null),
  })

  useEffect(() => {
    getPana()
    checkAgreementLetter()
  }, [])

  useEffect(() => {
    if (!errorProps) {
      return
    }

    scrollToElement(errorProps)
  }, [errorProps])

  // Effect terpisah untuk getLetter - hanya trigger saat approved berubah
  useEffect(() => {
    getLetter(approved)
  }, [approved])

  // Effect terpisah untuk handleStepData - update parent state
  useEffect(() => {
    handleStepData({
      step: 4,
      is_surat_pernyataan: approved,
      is_file_uploaded: !!uploadedFileUrl,
    })
  }, [approved, uploadedFileUrl])

  const getLetter = (approved: any) => {
    setLoading(true)
    api
      .statementLetter({is_setuju: approved ? 1 : 0})
      .then((response: any) => {
        setLoading(false)
        const {data} = response.data
        setFilePdf(data?.file)
        // Tidak perlu getPana() di sini karena sudah di initial load
        // getPana() hanya dipanggil saat upload selesai
      })
      .catch((error: any) => {
        setLoading(false)
        const {data, status} = error.response
        setErrors(data?.meta?.error)

        handleApiError(error, navigate)
        toast.error(data?.meta?.message)
      })
  }

  const checkAgreementLetter = () => {
    setLoading(true)
    api
      .checkAgreementLetter()
      .then((response: any) => {
        setLoading(false)
        const {data} = response.data

        setApproved(data)
      })
      .catch((error: any) => {
        setLoading(false)
        const {data, status} = error.response
        setErrors(data?.meta?.error)

        handleApiError(error, navigate)
        toast.error(data?.meta?.message)
      })
  }

  const onDocumentLoadSuccess = ({numPages}: any) => {
    setNumPages(numPages)
  }

  const getPana = () => {
    setLoading(true)
    api
      .getRegisterPana()
      .then((response: any) => {
        setLoading(false)
        const {data} = response.data
        setDataPana(data)
        
        // Set file URL jika ada dari backend DAN berbeda dengan yang sekarang
        // Ini mencegah infinite loop
        const fileUrl = data?.fileKomitmen?.file
        if (fileUrl && fileUrl !== uploadedFileUrl) {
          setUploadedFileUrl(fileUrl)
        }
      })
      .catch((error: any) => {
        setLoading(false)
        const {data, status} = error.response

        handleApiError(error, navigate)
        toast.error(data?.meta?.message)
        setErrors(data?.meta?.error)
      })
  }

  const handleAgreement = () => {
    setIsShow(true)
  }

  const handleApprove = () => {
    setApproved(true)
    setIsShow(false)
  }

  const scrollToElement = (errors: any) => {
    const firstErrorField = Object.keys(errors)[0]
    const target = fieldRefs[firstErrorField]

    if (target && target.current) {
      const yOffset = -80 // jika ada header
      const y = target.current.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({top: y, behavior: 'smooth'})
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type (PDF only)
      if (file.type !== 'application/pdf') {
        toast.error('File harus berformat PDF')
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB')
        return
      }
      setUploadedFile(file)
    }
  }

  const handleUploadFile = () => {
    if (!uploadedFile) {
      toast.error('Silakan pilih file terlebih dahulu')
      return
    }

    setIsUploading(true)
    api
      .uploadFile({upload_file: uploadedFile, tipe: 'surat_pernyataan_komitmen'})
      .then((response: any) => {
        setIsUploading(false)
        toast.success('Surat pernyataan berhasil diupload')
        
        // Ambil data terbaru dari backend
        getPana()
      })
      .catch((error: any) => {
        setIsUploading(false)
        const {data} = error.response
        toast.error(data?.meta?.message || 'Gagal upload surat pernyataan')
        handleApiError(error, navigate)
      })
  }

  const handleOpenEmeterai = () => {
    window.open('https://mitracomm.e-meterai.co.id/', '_blank')
  }

  const handleDownloadPdf = () => {
    if (filePdf) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = filePdf
      const namaPendaftar = dataPana?.users?.nama_lengkap || dataPana?.nama_lengkap || 'Pendaftar'
      const fileName = `Surat_Pernyataan_${namaPendaftar.replace(/\s+/g, '_')}.pdf`
      link.setAttribute('download', fileName)
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      toast.success('Surat Pernyataan sedang diunduh')
    } else {
      toast.error('File surat pernyataan tidak tersedia')
    }
  }

  return (
    <>
      <Modal show={isShow} centered size='sm'>
        <div className='p-10'>
          <p className='h6 text-center'>Konfirmasi Persetujuan</p>
          <p className='mt-5'>Apakah yakin ingin menyetujui surat pernyataan ini?</p>

          <button
            type='button'
            className='btn btn-primary btn-sm w-100 mt-5'
            style={{background: ' var(--Primary-L-500, #F95E2F)'}}
            onClick={handleApprove}
          >
            Ya, Setujui
          </button>

          <button
            type='button'
            className='btn btn-sm w-100 mt-3 text-primary'
            style={{border: '1px solid var(--Primary-L-500, #F95E2F)'}}
            onClick={() => setIsShow(false)}
          >
            Batal
          </button>
        </div>
      </Modal>

      <div className='py-8 px-15'>
        <div className='w-20px'>
          <div
            className='d-flex justify-content-center text-primary rounded-circle px-4 py-1'
            style={{background: 'var(--Primary-L-50, #F9EAE8)'}}
          >
            {enabledSteps.indexOf(currentStep) + 1}
          </div>
        </div>

        <p className='h4 mt-8' style={{color: '#64738D'}}>
          Persetujuan Surat Pernyataan
        </p>

        <div className='d-flex mt-5 justify-content-space-between'>
          {enabledSteps.map((stepNum, index) => {
            const currentStepIndex = enabledSteps.indexOf(currentStep)
            const isCompleted = index <= currentStepIndex
            return (
              <div key={stepNum} className={`progress-step-container ${index > 0 ? 'px-2' : ''}`}>
                <div className={`progress h-7px rounded ${isCompleted ? 'bg-primary' : 'bg-secondary'}`}>
                  <div className={`progress-bar rounded ${isCompleted ? 'bg-primary' : 'bg-secondary'} fs-7 fw-bold`} role='progressbar' />
                </div>
              </div>
            )
          })}
        </div>

        {filePdf ? (
          <div
            style={{boxShadow: '0px 10px 20px 0px #605DD126', maxWidth: '800px', margin: 'auto'}}
            className='mt-15'
          >
            <Document file={filePdf} onLoadSuccess={onDocumentLoadSuccess}>
              {numPages &&
                Array.from(new Array(numPages), (el, index) => (
                  <div key={`page_${index + 1}`} style={{marginBottom: '40px'}}>
                    <Page pageNumber={index + 1} width={600} />
                    {index + 1 < numPages && (
                      <hr style={{marginTop: '20px', border: '1px solid #ccc'}} />
                    )}
                  </div>
                ))}
            </Document>
          </div>
        ) : null}

        {!approved ? (
          <div className='text-center' ref={fieldRefs.is_surat_pernyataan}>
            <button type='button' className='btn btn-sm btn-primary mt-10' onClick={() => handleAgreement()}>
              Saya Setuju
            </button>

            {errorProps?.is_surat_pernyataan ? (
              <p className='text-danger mt-1'>{errorProps?.is_surat_pernyataan}</p>
            ) : null}
          </div>
        ) : (
          <div className='mt-10' style={{maxWidth: '800px', margin: 'auto'}}>
            <div className='alert alert-info d-flex align-items-center' role='alert'>
              <i className='bi bi-info-circle-fill me-3 fs-3'></i>
              <div>
                <strong>Langkah Selanjutnya:</strong>
                <p className='mb-0'>Silakan unduh surat pernyataan di atas, cetak, tandatangani, bubuhi materai elektronik, lalu upload kembali.</p>
              </div>
            </div>

            {!uploadedFileUrl && (
              <div className='alert alert-warning mt-3' role='alert'>
                <div className='d-flex align-items-start gap-2'>
                  <i className='bi bi-exclamation-triangle-fill fs-5 mt-1 flex-shrink-0'></i>
                  <div>
                    <strong className='d-block mb-1'>Perhatian!</strong>
                    <p className='mb-0' style={{fontSize: '0.9rem'}}>
                      Anda harus mengupload surat pernyataan bermaterai sebelum dapat melanjutkan ke tahap berikutnya.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className='card shadow-sm mt-5'>
              <div className='card-body p-4 p-md-5'>
                <h5 className='card-title mb-4 fw-bold'>Upload Surat Pernyataan Bermaterai</h5>
                
                {/* Tampilkan file yang sudah diupload */}
                {uploadedFileUrl && (
                  <div className='alert alert-success mb-4' role='alert'>
                    <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3'>
                      <div className='flex-grow-1'>
                        <div className='d-flex align-items-center mb-2'>
                          <i className='bi bi-check-circle-fill me-2 fs-5 text-success'></i>
                          <strong className='fs-6'>File sudah diupload!</strong>
                        </div>
                        <p className='mb-0 ms-4 ms-md-0 text-dark' style={{fontSize: '0.9rem'}}>
                          Surat pernyataan Anda sudah tersimpan. Anda dapat melanjutkan ke tahap berikutnya atau mengupload ulang jika ingin mengganti file.
                        </p>
                      </div>
                      <a 
                        href={uploadedFileUrl} 
                        target='_blank' 
                        rel='noopener noreferrer'
                        className='btn btn-success btn-sm d-flex align-items-center gap-1 flex-shrink-0 w-100 w-md-auto justify-content-center'
                        style={{minWidth: '120px'}}
                      >
                        <i className='bi bi-eye'></i>
                        <span>Lihat File</span>
                      </a>
                    </div>
                  </div>
                )}
                
                <div className='mb-4'>
                  <div className='d-flex flex-column flex-sm-row gap-2 mb-3'>
                    <button 
                      type='button'
                      className='btn btn-info d-flex align-items-center justify-content-center gap-2'
                      onClick={handleDownloadPdf}
                      disabled={!filePdf}
                    >
                      <i className='bi bi-download'></i>
                      <span>Unduh Surat Pernyataan</span>
                    </button>
                    <button 
                      type='button'
                      className='btn btn-success d-flex align-items-center justify-content-center gap-2'
                      onClick={handleOpenEmeterai}
                    >
                      <i className='bi bi-box-arrow-up-right'></i>
                      <span>Buka E-Meterai</span>
                    </button>
                  </div>
                  <div className='alert alert-light border mb-0 py-3 px-3' style={{fontSize: '0.9rem', color: '#000'}}>
                    <i className='bi bi-info-circle me-2'></i>
                    <strong>Langkah:</strong>
                    <ol className='mb-0 mt-2 ps-4'>
                      <li>Unduh surat pernyataan</li>
                      <li>Tekan tombol "Buka E-Materai"</li>
                      <li>Login / Buat akun</li>
                      <li>Bubuhi Dokumen dengan E-Materai</li>
                      <li>Upload Kembali</li>
                    </ol>
                  </div>
                </div>

                <div className='mb-3'>
                  <label className='form-label fw-bold'>Pilih File Surat Pernyataan (PDF)</label>
                  <input 
                    type='file' 
                    className='form-control' 
                    accept='application/pdf'
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  <small className='text-muted d-block mt-1' style={{fontSize: '0.85rem'}}>
                    Format: PDF, Maksimal 5MB
                  </small>
                </div>

                <button 
                  type='button'
                  className='btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 py-2'
                  onClick={handleUploadFile}
                  disabled={!uploadedFile || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                      <span>Mengupload...</span>
                    </>
                  ) : (
                    <>
                      <i className='bi bi-cloud-upload'></i>
                      <span>Upload Surat Pernyataan</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const Step4: FC<Props> = ({handleStepData, errorProps, asTeam, enabledSteps, currentStep}) => {
  return <Step4Page handleStepData={handleStepData} errorProps={errorProps} asTeam={asTeam} enabledSteps={enabledSteps} currentStep={currentStep} />
}

export default Step4
